import { CommonModule, KeyValue } from '@angular/common';
import { Component, computed, effect, OnDestroy, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import html2canvas from 'html2canvas';
import jspdf from 'jspdf';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { Observable, ObservableInput, of, Subject } from 'rxjs';
import { catchError, finalize, map, takeUntil } from 'rxjs/operators';
import { NewCaseService } from 'src/app/cases/new-case/new-case.service';
import { AppService } from 'src/app/services/app.service';
import { EntitySearchService } from 'src/app/services/entity-search.service';
import { KEYS, MessageToUiService, } from 'src/app/services/message-to-ui.service';
import { SettingsService } from 'src/app/services/settings.service';
import { CustomSelectComponent } from 'src/app/shared/custom-select/custom-select.component';
import { FileUploadComponent } from 'src/app/shared/file-upload/file-upload.component';
import { FirmService } from 'src/app/shared/firm/firm.service';
import { GlobalSpinnerService } from 'src/app/shared/global-spinner/global-spinner.service';
import { LocalizationService, LocalizationSettings, } from 'src/app/shared/localization/localization.service';
import { AllowedNewCaseTypeEnum, ICustomerSummary, IEntitySearchRequest, IEntitySearchResult, INewCase, INewInvoice } from 'src/app/shared/models';
import { JsonDateParsePipe } from 'src/app/shared/pipes/json-date-parse.pipe';
import { deepCopy } from 'src/app/shared/utilities';
import { UtilsService } from 'src/app/shared/utils.service';
import { ValidatorService } from 'src/app/shared/validator.service';
import { InvoiceCopiesComponent } from './invoice-copies/invoice-copies.component';
import { RecentlyCreatedCasesComponent } from './recently-created-cases/recently-created-cases.component';
import { LoadingManager } from 'src/app/services/loading-manager.service';
import { InvoiceReminderContext, InvoiceReminderDateBounds, NewCaseValidators, NewCaseValidatorsDenmark, NewCaseValidatorsFinland, NewCaseValidatorsNorway } from './new-case-validators';

@Component({
  selector: 'app-new-case',
  templateUrl: './new-case.component.html',
  styleUrls: ['./new-case.component.scss'],
  host: {
    '(window:resize)': 'onWindowResize($event)'
  },
  imports: [CommonModule, TranslateModule, MatTooltipModule, FormsModule, CustomSelectComponent,
    InputNumberModule, DatePickerModule, JsonDateParsePipe, RecentlyCreatedCasesComponent]
})
export class NewCaseComponent implements OnInit, OnDestroy {
  constructor(
    private translate: TranslateService,
    private service: NewCaseService,
    private localization: LocalizationService,
    public validator: ValidatorService,
    private messageToUi: MessageToUiService,
    private settingService: SettingsService,
    private application: AppService,
    private utilsService: UtilsService,
    private entitiesSearchService: EntitySearchService,
    private firmService: FirmService,
    private spinner: GlobalSpinnerService,
    public matDialog: MatDialog,
    private loadingManager: LoadingManager
  ) { }



  @ViewChild(FileUploadComponent) invoiceCopyFileUpload: FileUploadComponent;
  private defaultNewCase: INewCase;
  private readonly unsubscribe$ = new Subject();
  private _invoiceCopies: File[] = [];

  newCase: INewCase;
  locale: LocalizationSettings = {} as LocalizationSettings;
  countries$ = this.service.getCountries().pipe(map(_ => _.map(x => ({ key: x.code, value: x.name }))));
  currencyCodes$ = this.service.getCurrencies().pipe(map(_ => this.utilsService.toDefaultKeyValue(_)));
  yesNoSelection: KeyValue<boolean, string>[] = [];
  invoicesTotal: number = 0;
  invoicesErrors: string[] = [];

  #settings = toSignal(
    this.loadingManager.observeSingle(
      of(null),
      () => this.settingService.getMultipleSettingsByName([
        'Case_Collection_Person_MaxCustomerReminderFee',
        'Case_Collection_Company_MaxCustomerReminderFee',
        'Case_Reminder_MaxCustomerReminderFee',
        'NewCase_IsSocialOrOrganizationNumberRequired',
        'NewCase_DisplayRoadFreightQuestion',
        'NewCase_CanReminderIncludeReminderFeeAndEuFee',
        'NewCase_NoSameDayReminderAsDueDate',
        'NewCase_ApplyEUFee14DayPaymentRule',
        'NewCase_DisplayEuFeeAndReminderFeeInSecondStep',
        'NewCase_HasReminderWithCustomDaysPayment',
        'NewCase_CompanyCustomReminderDaysPayment',
        'NewCase_CompanyStandardReminderDaysPayment',
        'KreditorWeb_NewCase_CollectionReminderCompulsory',
        'KreditorWeb_NewCase_CanCollectFeesInReminder',
        'CanSpecifyCreateCaseEuFee',
        'PostCodeLength',
        'KreditorWeb_NewCase_ReminderFeePerInvoice'
      ].join(';')),
      this.unsubscribe$.asObservable() as ObservableInput<never>
    ),
    {
      initialValue: {} as Record<string, string>
    }
  );

  isSocialNumberRequired = computed(() =>
    this.#settings()['NewCase_IsSocialOrOrganizationNumberRequired']?.isTruthy()
  );

  displayRoadFreightQuestion = computed(() =>
    this.#settings()['NewCase_DisplayRoadFreightQuestion']?.isTruthy()
  );

  canReminderIncludeReminderFeeAndEuFee = computed(() =>
    this.#settings()['NewCase_CanReminderIncludeReminderFeeAndEuFee']?.isTruthy()
  );

  noSameDayReminderAsDueDate = computed(() =>
    this.#settings()['NewCase_NoSameDayReminderAsDueDate']?.isTruthy()
  );

  applyEUFee14DayPaymentRule = computed(() =>
    this.#settings()['NewCase_ApplyEUFee14DayPaymentRule']?.isTruthy()
  );

  isDisplayedEuFeeAndReminderFeeInSecondStep = computed(() =>
    this.#settings()['NewCase_DisplayEuFeeAndReminderFeeInSecondStep']?.isTruthy()
  );

  hasReminderWithCustomDaysPayment = computed(() =>
    this.#settings()['NewCase_HasReminderWithCustomDaysPayment']?.isTruthy() ?? false
  );

  // the defaults (10 custom, 14 standard) are the values used before the days became settings
  reminderPaymentDays = computed(() =>
    this.hasReminderWithCustomDaysPayment()
      ? this.#settings()['NewCase_CompanyCustomReminderDaysPayment']?.toNumberOrDefault(10) ?? 10
      : this.#settings()['NewCase_CompanyStandardReminderDaysPayment']?.toNumberOrDefault(14) ?? 14
  );

  collectionReminderCompulsorySignal = computed(() =>
    this.#settings()['KreditorWeb_NewCase_CollectionReminderCompulsory']?.isTruthy()
  );

  canCollectFeesInReminderSignal = computed(() =>
    this.#settings()['KreditorWeb_NewCase_CanCollectFeesInReminder']?.isTruthy()
  )

  canAddEuFee = computed(() =>
    this.#settings()['CanSpecifyCreateCaseEuFee']?.isTruthy()
  );

  #postCodeLength = computed(() =>
    this.#settings()['PostCodeLength']?.toNumberOrDefault(0)
  );

  shouldDisplayMaximumReminderFee: WritableSignal<boolean> = signal(false)
  canAddCustomerReminderFee = toSignal(this.firmService.getClaimSettings().pipe(map(_ => _?.isCustomerReminderFeeEnabled)));
  maxCustomerReminderFeeSignal: WritableSignal<number> = signal(Number.MAX_SAFE_INTEGER);
  maxReminderFeeInfoSignal = signal<string>('');

  customers$: Observable<KeyValue<number, string>[]>;
  caseRoles = signal([{ value: 'NewCase.Reminder', key: 'Reminder' }, { value: 'NewCase.Collection', key: 'Collection' }]);
  customers: ICustomerSummary[];
  activeTab = signal(0);
  verification1: boolean = false;
  verification2: boolean = false;
  verification3: boolean = false;
  verification4: boolean = false;
  verification5: boolean = false;
  postCodeMaxLength = signal<number | null>(null);
  isPostCodeRequired = signal(true);
  isInitialInformationComplete = signal(false);
  isDebtorInformationComplete = signal(false);
  isInvoicesInformationComplete = signal(false);
  isNewCaseTypeDropdownDisabled = signal(false);
  errorsPersonDebtCollectionNotice: WritableSignal<string[]> = signal([]);
  errorsCompanyDebtCollectionNotice: WritableSignal<string[]> = signal([]);
  minDateOfReminder: Date | null = null;
  isSmallScreen: boolean = window.innerWidth < 600;
  selectedCustomerSignal = signal<ICustomerSummary | null>(null);
  isFirmSignal = signal(false);
  newInvoicesSignal = signal<INewInvoice[]>([]);

  countryCodeSignal = toSignal(this.firmService.getFirmCountryCode());
  newCaseValidator = computed<NewCaseValidators | undefined>(() => {
    switch (this.countryCodeSignal()) {
      case 'NO':
        return new NewCaseValidatorsNorway();
      case 'FI':
        return new NewCaseValidatorsFinland();
      case 'DK':
        return new NewCaseValidatorsDenmark();
    }
  });

  // per invoice instead of per case
  isPerInvoiceReminder = computed(() =>
    this.#settings()['KreditorWeb_NewCase_ReminderFeePerInvoice']?.isTruthy()
  );

  invoiceReminderErrors = signal<Record<number, string[]>>({});
  invoiceReminderBounds = signal<Record<number, InvoiceReminderDateBounds>>({});

  showInvoiceReminderColumns(): boolean {
    return this.canHaveInvoiceReminders() && !(this.canHaveFinlandEuFee() && this.newCase.euFee != null);
  }

  isInvoiceReminderDateRequired(invoice: INewInvoice): boolean {
    return (invoice.customerReminderFee ?? 0) > 0;
  }

  showFinlandEuFee(): boolean {
    const hasInvoiceReminderFee = this.canHaveInvoiceReminders() && this.newCase.invoices.some(i => this.isInvoiceReminderDateRequired(i));
    return this.canHaveFinlandEuFee()
      && (this.canReminderIncludeReminderFeeAndEuFee() || this.newCase.euFee != null || !hasInvoiceReminderFee);
  }

  showStepTwoEuFee(): boolean {
    if (this.isPerInvoiceReminder()) {
      return this.showFinlandEuFee();
    }

    return !!(this.canAddEuFee() && this.isDisplayedEuFeeAndReminderFeeInSecondStep() && this.newCase.isFirm && this.newCase.hasDebtCollectionNoticeEUFee);
  }

  isFinlandEuFeeValid(): boolean {
    return !this.showFinlandEuFee() || this.newCase.euFee == null || this.newCase.euFee > 0;
  }
  onIsFirmChanged(value: boolean): void {
    this.newCase.isFirm = value;
    this.isFirmSignal.set(value);
  }

  customInvoiceInterest = computed(() => {
    const customer = this.selectedCustomerSignal();

    return !!(
      customer &&
      this.isFirmSignal() &&
      customer.allowSeparateInterestRatesInCreditorWeb
    );
  });

  isInterestRateRequired = computed(() => {
    if (!this.customInvoiceInterest()) return false;
    return this.newInvoicesSignal().some(inv => Number.isFinite(inv.interestRate));
  });


  collectionReminderCompulsoryEffect = effect(() => {
    if (this.collectionReminderCompulsorySignal() && !this.isPerInvoiceReminder()) {
      this.newCase.debtorHasReminderWithPaymentDeadline = true;
    }
  });

  ngOnInit(): void {
    this.localization.settings$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((_) => {
        this.locale = _;
      });

    this.service.getDefaults()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((defaultCase) => {
        this.defaultNewCase = defaultCase;
      });

    this.yesNoSelection = this.getYesNoSelectionOptions();
    this.resetValues();

    this.setCustomers();
    this.getIsSmallScreen();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  determineIsInformationComplete() {
    this.syncFinlandReminderFlags();

    switch (this.activeTab()) {
      case 0:
        this.isInitialInformationComplete.set(this.newCase.customerNumber > 0)
        break;
      case 1:
        this.isDebtorInformationComplete.set(
          this.newCase.name?.length > 0 &&
          this.newCase.address1?.length > 0 &&
          this.newCase.customerReference1?.length > 0 &&
          (!this.isPostCodeRequired() || this.newCase.postCode?.length > 0) &&
          (!this.isSocialNumberRequired() || this.newCase.socialNumber?.length > 0));
        break;
      case 2:
        this.isActiveTabTwoValid();
        break;
    }
  }

  clearAdditionalInfo() {
    if (!this.newCase.debtorHasObjections)
      this.newCase.additionalDetails = null;
  }

  onAddInvoice() {
    this.service
      .getInvoiceDefaults()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((_) => {
        this.newCase.invoices.push(_);
        this.newInvoicesSignal.update((invoices) => [...invoices, _]);
      });
    this.calculateTotal();
    this.determineIsInformationComplete()
  }

  onRemoveInvoice(index: number) {
    if (index != null) {
      this.newCase.invoices = this.newCase.invoices.filter(
        (item, i) => i !== index
      );
    }
    this.calculateTotal();
    this.setMinDateOfReminder();
    this.determineIsInformationComplete()
  }

  onRemoveInvoiceCopy(invoice: INewInvoice) {
    this._invoiceCopies = this._invoiceCopies.filter(x => x.name != invoice.invoiceCopyName);
    invoice.invoiceCopyName = null;
    this.setMinDateOfReminder();
    this.determineIsInformationComplete();
  }

  async onSubmit(f: NgForm) {
    this.invoicesErrors = [];

    this.sanitizeFinlandReminderFields();
    this.adjustInvoiceDates();
    this.adjustDateOfReminder();
    this.sanitizeInterestRates();

    this.spinner.show();

    let confirmationFile = await this.getConfirmationPdf();

    this.service.createCase(this.newCase, this._invoiceCopies, confirmationFile)
      .pipe(finalize(() => this.spinner.hide()))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (_) => {
          this.messageToUi.success(`${this.translate.instant(KEYS.NewCase_Inserted_CaseNumber)} ${_.caseNumber.toString()}`, undefined, true);
          if (!_.attachmentsUploadSuccess) {
            this.messageToUi.error(KEYS.NewCase_Errors_FailedToUploadAttachment, _.failedUploadDocuments.join(', '));
          }
          this.resetValues();
        },
        error: (e) => {
          this.invoicesErrors = this.validator.getPropertyValidationErrors(e, 'invoices').map(x => x.errorCode);
          this.validator.handleError(f, e, this.invoicesErrors.length == 0);
        }
      });
  }

  determinePostCodeRestrictions(): void {
    const isLocalCountry = this.newCase.countryCode === this.countryCodeSignal();

    this.postCodeMaxLength.set(isLocalCountry ? this.#postCodeLength() : null);
    this.isPostCodeRequired.set(isLocalCountry);
  }

  onNavigateToTab(tabNumber: number) {
    switch (tabNumber) {
      case 0:
        this.activeTab.set(0);
        break;
      case 1:
        if (this.isInitialInformationComplete()) { this.activeTab.set(1); }
        break;
      case 2:
        if (this.isDebtorInformationComplete()) {
          this.activeTab.set(2);
          this.checkStepTwoFieldsBaseOnDebtorAndCaseRole();
        }
        break;
      case 3:
        if (this.isDebtorInformationComplete() && this.isInvoicesInformationComplete()) { this.activeTab.set(3); }
      default: break;
    }
  }

  adjustInvoiceDates() {
    this.newCase.invoices.map(i => i.date = i.date != undefined ? new Date(i.date.getTime() - (i.date.getTimezoneOffset() * 60000)) : null);
    this.newCase.invoices.map(i => i.dueDate = i.dueDate != undefined ? new Date(i.dueDate.getTime() - (i.dueDate.getTimezoneOffset() * 60000)) : null);
    this.newCase.invoices.map(i => i.customerReminderDueDate = i.customerReminderDueDate != undefined ? new Date(i.customerReminderDueDate.getTime() - (i.customerReminderDueDate.getTimezoneOffset() * 60000)) : null);
  }

  onInvoiceReminderFeeChange(invoice: INewInvoice, fee: number | null) {
    invoice.customerReminderFee = fee;

    if (fee == null) {
      invoice.customerReminderDueDate = null;
    }
  }

  onInvoiceFieldLeave(field: string | null = null) {
    this.invoicesErrors = [];

    if (field == 'amount') {
      this.calculateTotal();
    }
    this.determineIsInformationComplete();
  }

  calculateTotal() {
    let totalAmount = 0;
    this.newCase.invoices.forEach((i) => (totalAmount += i.amount));
    this.invoicesTotal = totalAmount;
  }

  getYesNoSelectionOptions(): KeyValue<boolean, string>[] {
    return [{ key: true, value: 'NewCase.SendReminder.Yes' },
    { key: false, value: 'NewCase.SendReminder.No' }];
  }

  debtorInfoSearch(socialNumber: string, customerReference1: string) {
    if (socialNumber?.length == 0 && customerReference1?.length == 0) { return; }

    const entitySearchRequest = {
      socialNumber,
      customerReference: customerReference1,
      customerNumber: this.newCase.customerNumber,
      usePostadresseIfAvailable: false
    } as IEntitySearchRequest;

    this.entitiesSearchService.getEntitySearchResult(entitySearchRequest)
      .pipe(takeUntil(this.unsubscribe$), catchError(_ => { return of({} as IEntitySearchResult) }))
      .subscribe({
        next: result => {
          if (result.name) {
            if (socialNumber != null) {
              result.socialNumber = socialNumber;
            }
            this.mapToCreateCaseModel(result);
            this.determineIsInformationComplete();
          }
        }
      });
  }

  updateReminderFeeLimit() {
    const role = this.newCase.caseRole;

    switch (role) {
      case 'Collection':
        const collectionPersonLimit = this.#settings()["Case_Collection_Person_MaxCustomerReminderFee"]?.toNumberOrDefault(Number.MAX_VALUE);
        const collectionCompanyLimit = this.#settings()["Case_Collection_Company_MaxCustomerReminderFee"]?.toNumberOrDefault(Number.MAX_VALUE);
        let limit = this.newCase.isFirm ? collectionCompanyLimit : collectionPersonLimit;

        this.maxCustomerReminderFeeSignal.set(limit ?? Number.MAX_VALUE);
        this.maxReminderFeeInfoSignal.set(this.translate.instant(
          'NewCase.MaxAllowedReminderFee',
          {
            maxFee: limit
          }))
        break;
      case 'Reminder':
        const reminderLimit = this.#settings()["Case_Reminder_MaxCustomerReminderFee"]?.toNumberOrDefault(Number.MAX_VALUE);

        this.maxCustomerReminderFeeSignal.set(reminderLimit);
        this.maxReminderFeeInfoSignal.set(this.translate.instant(
          'NewCase.MaxAllowedReminderFee',
          {
            maxFee: reminderLimit
          }))
        break;
      default:
        this.maxCustomerReminderFeeSignal.set(Number.MAX_VALUE);
        this.maxReminderFeeInfoSignal.set('');
        break;
    }

    this.shouldDisplayMaximumReminderFee.set(this.maxCustomerReminderFeeSignal() != Number.MAX_VALUE)
  }

  onCustomerChange() {
    if (this.newCase.customerNumber == null) {
      this.selectedCustomerSignal.set(null);
      return;
    }

    const selectedCustomer = this.customers.find(x => x.number == this.newCase.customerNumber);
    this.selectedCustomerSignal.set(selectedCustomer ?? null);

    if (selectedCustomer?.allowedNewCaseType == AllowedNewCaseTypeEnum.Option || !selectedCustomer?.allowedNewCaseType) {
      this.newCase.caseRole = selectedCustomer?.defaultCaseRole ?? 'Collection';
      this.isNewCaseTypeDropdownDisabled.set(false);

      return;
    }

    if (selectedCustomer.allowedNewCaseType != AllowedNewCaseTypeEnum.Option) {
      selectedCustomer.allowedNewCaseType == AllowedNewCaseTypeEnum.Reminder ? this.newCase.caseRole = 'Reminder' : this.newCase.caseRole = 'Collection';
      this.isNewCaseTypeDropdownDisabled.set(true);
    }
  }

  onPreviewCopy(copyName: string) {
    const file = this._invoiceCopies.find(x => x.name == copyName);
    window.open(URL.createObjectURL(file!));
  }

  onWindowResize() {
    this.getIsSmallScreen();
  }

  whenReminderWithPaymentDeadlineChange(): void {

    if (!this.newCase.debtorHasReminderWithPaymentDeadline) {
      this.newCase.dateOfReminder = null;
      this.newCase.debtorHasReminderIncludedFee = false;
      this.newCase.hasDebtCollectionNoticeEUFee = false;
      this.newCase.customerReminderFee = undefined;
      this.newCase.euFee = undefined;
    }

    this.determineIsInformationComplete();
  }

  whenDebtorHasReminderIncludedFeeChange(): void {

    if (!this.canReminderIncludeReminderFeeAndEuFee() && this.newCase.debtorHasReminderIncludedFee) {
      this.newCase.hasDebtCollectionNoticeEUFee = false;
    }

    if (!this.newCase.debtorHasReminderIncludedFee) {
      this.newCase.customerReminderFee = undefined;
    }
  }

  whenHasDebtCollectionNoticeEUFeeChange(): void {

    if (!this.canReminderIncludeReminderFeeAndEuFee() && this.newCase.hasDebtCollectionNoticeEUFee) {
      this.newCase.debtorHasReminderIncludedFee = false;
    }

    if (!this.newCase.hasDebtCollectionNoticeEUFee) {
      this.newCase.euFee = undefined;
    }
  }

  setMinDateOfReminder(): void {

    if (this.isPerInvoiceReminder()) {
      // check invoice reminder date with due date
      this.determineIsInformationComplete();
      return;
    }

    const maxInvoiceDueDate = this.newCase
      .invoices
      .map(x => x.dueDate)
      .reduce((maxDueDate, currentDueDate) => maxDueDate == null || (currentDueDate != null && currentDueDate > maxDueDate) ? currentDueDate : maxDueDate);

    if (maxInvoiceDueDate) {
      this.minDateOfReminder = new Date(maxInvoiceDueDate);
      this.minDateOfReminder.setDate(maxInvoiceDueDate.getDate() + (this.newCase.isFirm ? 1 : 0))
    }

    this.newCase.dateOfReminder = null;
    this.errorsPersonDebtCollectionNotice.set([]);
  }

  checkReminderNoticeDateWhenPerson(reminderNoticeDate: Date): boolean {
    let validator = this.newCaseValidator();

    if (!validator) {
      return false;
    }

    let result = validator!.checkReminderNoticePerson({
      today: new Date(),
      reminderNoticeDate: reminderNoticeDate,
      minDateOfReminder: this.minDateOfReminder ?? new Date(),
      isFirm: this.newCase.isFirm ?? false,
      debtorHasReminderWithPaymentDeadline: this.newCase.debtorHasReminderWithPaymentDeadline,
      debtorHasReminderIncludedFee: this.newCase.debtorHasReminderIncludedFee,
      hasReminderWithCustomDaysPayment: this.hasReminderWithCustomDaysPayment(),
      reminderPaymentDays: this.reminderPaymentDays(),
      hasDebtCollectionNoticeEUFee: this.newCase.hasDebtCollectionNoticeEUFee ?? false,
      applyEUFee14DayPaymentRule: this.applyEUFee14DayPaymentRule(),
    });

    this.errorsCompanyDebtCollectionNotice.set(result.errors);
    return result.success;
  }

  checkReminderNoticeDateWhenCompany(reminderNoticeDate: Date): boolean {
    let validator = this.newCaseValidator();

    if (!validator) {
      return false;
    }

    let result = validator!.checkReminderNotice({
      today: new Date(),
      reminderNoticeDate: reminderNoticeDate,
      minDateOfReminder: this.minDateOfReminder ?? new Date(),
      isFirm: this.newCase.isFirm ?? false,
      debtorHasReminderWithPaymentDeadline: this.newCase.debtorHasReminderWithPaymentDeadline,
      debtorHasReminderIncludedFee: this.newCase.debtorHasReminderIncludedFee,
      hasReminderWithCustomDaysPayment: this.hasReminderWithCustomDaysPayment(),
      reminderPaymentDays: this.reminderPaymentDays(),
      hasDebtCollectionNoticeEUFee: this.newCase.hasDebtCollectionNoticeEUFee ?? false,
      applyEUFee14DayPaymentRule: this.applyEUFee14DayPaymentRule(),
    });

    this.errorsCompanyDebtCollectionNotice.set(result.errors);
    return result.success;
  }

  private checkStepTwoFieldsBaseOnDebtorAndCaseRole(): void {

    this.syncFinlandReminderFlags();
    this.areInvoiceRemindersValid();

    if (this.newCase.caseRole == 'Collection' && !this.newCase.isFirm && !this.areIsPersonAndCaseTypeCollectionFieldsValid()) {
      this.isInvoicesInformationComplete.set(false);
      this.verification5 = false;
      this.newCase.hasDebtCollectionNoticeEUFee = false;
      this.errorsCompanyDebtCollectionNotice.set([]);
    }
    else if (this.newCase.isFirm && !this.areIsCompanyAndCaseTypeCollectionFieldsValid()) {
      this.isInvoicesInformationComplete.set(false);
      this.verification5 = false;
      this.verification4 = false;
      this.errorsPersonDebtCollectionNotice.set([]);
    }
    else if (this.newCase.isFirm && !this.areIsCompanyAndCaseTypeReminderFieldsValid()) {
      this.newCase.debtorHasReminderWithPaymentDeadline = false;
      this.newCase.dateOfReminder = null;
      this.newCase.debtorHasReminderIncludedFee = false;
      this.newCase.hasDebtCollectionNoticeEUFee = false;
      this.errorsPersonDebtCollectionNotice.set([]);
      this.verification4 = false;
      this.isInvoicesInformationComplete.set(false);
    }
    else {

      if (this.newCase.caseRole == 'Reminder') {
        this.newCase.debtorHasReminderWithPaymentDeadline = false;
        this.newCase.dateOfReminder = null;
        this.newCase.debtorHasReminderIncludedFee = false;
        this.newCase.hasDebtCollectionNoticeEUFee = false;
        this.verification4 = false;
      }

      this.errorsPersonDebtCollectionNotice.set([]);
      this.errorsCompanyDebtCollectionNotice.set([]);
      this.isActiveTabTwoValid();
    }

  }

  private isActiveTabTwoValid(): void {
    const areInvoiceRemindersValid = this.areInvoiceRemindersValid();

    this.isInvoicesInformationComplete.set(
      areInvoiceRemindersValid &&
      this.isFinlandEuFeeValid() &&
      this.newCase.invoicesDescription?.length > 0 &&
      this.verification1 &&
      (this.newCase.isFirm || (this.verification2 && this.verification3)) &&
      (!this.newCase.debtorHasObjections || (this.newCase?.additionalDetails?.length ?? 0) > 0) &&
      this.areAllInvoicesComplete() &&
      this.areIsPersonAndCaseTypeCollectionFieldsValid() &&
      this.areIsCompanyAndCaseTypeCollectionFieldsValid() &&
      this.areIsCompanyAndCaseTypeReminderFieldsValid()
    );
  }

  private setCustomers() {
    const validCustomers$ = this.application.customers$.pipe(map(_ => _?.filter(x => !x.isParent && !x.isBarred && !x.isBlockedNewCase)));
    this.customers$ = validCustomers$.pipe(map(_ => {
      this.customers = _;
      return _.map(x => ({ key: x.number, value: `${x.number} - ${x.name}` }));
    }));
  }

  private areAllInvoicesComplete(): boolean {
    const areBasicFieldsValid = this.newCase.invoices
      .filter(x => !x.amount ||
        x.currencyCode?.length == 0 ||
        x.date == null ||
        x.dueDate == null ||
        !x.number ||
        x.invoiceCopyName == null
      )
      .length == 0;
    return areBasicFieldsValid && this.areInterestRatesValid();
  }

  private areIsPersonAndCaseTypeCollectionFieldsValid(): boolean {

    if (this.isPerInvoiceReminder()) {
      // check dates per invoice
      return !(this.newCase.caseRole == 'Collection' && !this.newCase.isFirm && this.newCase.debtorHasReminderWithPaymentDeadline && !this.verification4);
    }

    if (this.newCase.caseRole == 'Collection' && !this.newCase.isFirm && this.newCase.debtorHasReminderWithPaymentDeadline) {

      if (!this.newCase.dateOfReminder || !this.checkReminderNoticeDateWhenPerson(this.newCase.dateOfReminder) || !this.verification4) {
        return false;
      }
    }
    this.errorsPersonDebtCollectionNotice.set([]);
    return true;
  }

  private areIsCompanyAndCaseTypeCollectionFieldsValid(): boolean {

    if (this.isPerInvoiceReminder()) {
      return true; // checked per invoice in areInvoiceRemindersValid
    }

    if (this.newCase.caseRole == 'Collection' && this.newCase.isFirm && this.newCase.debtorHasReminderWithPaymentDeadline) {

      if (!this.newCase.dateOfReminder || !this.checkReminderNoticeDateWhenCompany(this.newCase.dateOfReminder)) {
        return false;
      }
    }

    this.errorsCompanyDebtCollectionNotice.set([]);
    return true;
  }

  private areIsCompanyAndCaseTypeReminderFieldsValid(): boolean {

    if (this.newCase.isFirm && this.newCase.caseRole == 'Reminder' && !this.verification5) {
      return false;
    }

    this.errorsCompanyDebtCollectionNotice.set([]);
    return true;
  }

  private adjustDateOfReminder(): void {

    if (this.newCase.debtorHasReminderWithPaymentDeadline && this.newCase.caseRole == 'Collection' && this.newCase.dateOfReminder) {
      this.newCase.dateOfReminder = new Date(this.newCase.dateOfReminder.getTime() - (this.newCase.dateOfReminder.getTimezoneOffset() * 60000))
    }
  }

  private async getConfirmationPdf(): Promise<Blob> {
    const data = document.getElementById('dataReview');
    var divBoundingRectangle = data!.getBoundingClientRect();

    return html2canvas(data!).then(canvas => {
      const contentDataURL = canvas.toDataURL('image/png')
      const pdf = new jspdf('p', 'mm', 'a4');

      let pixelToMm = 0.264583333;//at 97 dpi
      let a4MmWidth = 210;
      let a4MmHeight = 297;

      let mappedImageHeight = divBoundingRectangle.height / divBoundingRectangle.width * divBoundingRectangle.height;

      let mappedImageWidth = a4MmHeight / a4MmWidth * divBoundingRectangle.width;

      let widthInPdf: number;
      let heightInPdf: number;

      if (divBoundingRectangle.width < divBoundingRectangle.height) {
        widthInPdf = mappedImageWidth * pixelToMm;
        heightInPdf = a4MmHeight;
      }
      else {
        widthInPdf = a4MmWidth;
        heightInPdf = mappedImageHeight * pixelToMm;
      }

      pdf.addImage(contentDataURL, 'PNG', 0, 0, widthInPdf, heightInPdf);
      pdf.close();

      return pdf.output('blob');
    })
  }

  private resetValues() {
    this.newCase = deepCopy(this.defaultNewCase);
    this.invoicesTotal = 0;
    this.activeTab.set(0);
    this.verification1 = false;
    this.verification2 = false;
    this.verification3 = false;
    this.verification4 = false;
    this._invoiceCopies = [];
    this.invoiceReminderErrors.set({});
    this.invoiceReminderBounds.set({});
    this.isInitialInformationComplete.set(false);
    this.isDebtorInformationComplete.set(false);
    this.isInvoicesInformationComplete.set(false);
    this.isFirmSignal.set(this.newCase?.isFirm ?? false);
  }

  private mapToCreateCaseModel(searchResult: IEntitySearchResult) {
    this.newCase.name = searchResult.name;
    this.newCase.address1 = searchResult.address;
    this.newCase.address2 = searchResult.addressLine2;
    this.newCase.countryCode = searchResult.countryCode;
    this.newCase.postCode = searchResult.postCode;
    this.newCase.emailAddress = searchResult.emailAddress;
    this.newCase.phoneNumber = searchResult.phoneNumber;
    if (searchResult.socialNumber != null) {
      this.newCase.socialNumber = searchResult.socialNumber;
    }
    else if (searchResult.birthDate != null) {
      this.newCase.socialNumber = new Date(searchResult.birthDate).toLocaleDateString('en-GB').replace(/\//g, '.');
    }
  }

  openInvoiceCopyMatchingDialog() {
    const dialogRef = this.matDialog.open(InvoiceCopiesComponent);

    dialogRef.componentInstance.invoiceList = deepCopy(this.newCase.invoices);
    dialogRef.componentInstance.files = this._invoiceCopies;

    dialogRef.afterClosed()
      .pipe(takeUntil(this.unsubscribe$), finalize(() => this.determineIsInformationComplete()))
      .subscribe(changes => {
        if (changes) {
          this.newCase.invoices = changes.invoices;
          this._invoiceCopies = changes.files.filter(x => this.newCase.invoices.findIndex(y => y.invoiceCopyName == x.name) > -1);
        }
      });
  }

  private getIsSmallScreen() {
    this.isSmallScreen = window.innerWidth < 600;
  }

  private areInterestRatesValid(): boolean {
    if (!this.customInvoiceInterest()) {
      return true;
    }
    if (this.isInterestRateRequired()) {
      return this.newInvoicesSignal().every(inv => Number.isFinite(inv.interestRate));
    }
    return true;
  }

  private sanitizeInterestRates() {
    if (!this.customInvoiceInterest()) {
      this.newCase.invoices.forEach(invoice => {
        invoice.interestRate = null;
      });
    }
  }

  private canHaveInvoiceReminders(): boolean {
    return this.isPerInvoiceReminder() && this.canAddCustomerReminderFee() === true && this.newCase?.caseRole == 'Collection';
  }

  private canHaveFinlandEuFee(): boolean {
    return !!(this.isPerInvoiceReminder() && this.canAddEuFee() && this.newCase?.isFirm && this.newCase?.caseRole == 'Collection');
  }

  // hide reminder questions when per invoice reminder is active
  private syncFinlandReminderFlags(): void {
    if (!this.isPerInvoiceReminder()) {
      return;
    }

    const hasInvoiceReminder = this.showInvoiceReminderColumns()
      && this.newCase.invoices.some(i => i.customerReminderFee != null);

    this.newCase.debtorHasReminderWithPaymentDeadline = hasInvoiceReminder;
    this.newCase.debtorHasReminderIncludedFee = hasInvoiceReminder
      && this.newCase.invoices.some(i => (i.customerReminderFee ?? 0) > 0);
    this.newCase.hasDebtCollectionNoticeEUFee = this.showFinlandEuFee() && this.newCase.euFee != null;
    this.newCase.dateOfReminder = null;
    this.newCase.customerReminderFee = undefined;
  }

  private areInvoiceRemindersValid(): boolean {
    const validator = this.newCaseValidator();

    if (!(validator instanceof NewCaseValidatorsFinland) || !this.showInvoiceReminderColumns()) {
      this.invoiceReminderErrors.set({});
      this.invoiceReminderBounds.set({});
      return true;
    }

    const context: InvoiceReminderContext = {
      today: new Date(),
      isFirm: this.newCase.isFirm ?? false,
      hasDebtCollectionNoticeEUFee: this.newCase.hasDebtCollectionNoticeEUFee ?? false,
      hasReminderWithCustomDaysPayment: this.hasReminderWithCustomDaysPayment(),
      reminderPaymentDays: this.reminderPaymentDays(),
      applyEUFee14DayPaymentRule: this.applyEUFee14DayPaymentRule(), maxCustomerReminderFee: this.maxCustomerReminderFeeSignal(),
    };

    const errors: Record<number, string[]> = {};
    const bounds: Record<number, InvoiceReminderDateBounds> = {};

    this.newCase.invoices.forEach((invoice, i) => {
      bounds[i] = validator.getInvoiceReminderDateBounds(invoice, context);

      const result = validator.checkInvoiceReminder(invoice, context);
      if (!result.success) {
        errors[i] = result.errors;
      }
    });

    this.invoiceReminderErrors.set(errors);
    this.invoiceReminderBounds.set(bounds);
    return Object.keys(errors).length == 0;
  }

  // Don't send hidden fields
  private sanitizeFinlandReminderFields(): void {
    if (!this.isPerInvoiceReminder()) {
      return;
    }

    if (!this.showInvoiceReminderColumns()) {
      this.newCase.invoices.forEach(invoice => {
        invoice.customerReminderFee = null;
        invoice.customerReminderDueDate = null;
      });
    }

    if (!this.showFinlandEuFee()) {
      this.newCase.euFee = undefined;
    }

    this.syncFinlandReminderFlags();
  }
}
